import { Link } from 'react-router'
import { RiArrowRightLine, RiCheckLine } from 'react-icons/ri'
import { ROUTES } from '@/constants/routes'
import { useInView } from '@/hooks'

const services = [
  {
    id: 'antenatal',
    number: '01',
    title: 'Antenatal Empowerment Experience Package',
    availability: 'Available from 24 weeks of pregnancy — In person or Online',
    intro:
      'Birth is one of the most powerful and transformative experiences of your life and you deserve to feel informed, confident, and deeply supported every step of the way.',
    description:
      'The H.E.R Antenatal Empowerment Experience is a private, bespoke antenatal journey designed to help you feel calm, prepared, and truly empowered as you approach birth and early parenthood. In a safe space where your voice is heard and your choices are respected, these sessions gently guide you through everything you need to know — from understanding the signs of labour and your birth rights, to navigating interventions, creating your birth preferences, and preparing emotionally and practically for postpartum.',
    partnerNote:
      'Birth partners play a powerful and important role, yet many are left feeling unsure of how to help. Dedicated partner sessions are designed to change that — giving your partner the knowledge, confidence, and practical tools to support you calmly and effectively throughout labour, birth, and early postpartum.',
    includes: [
      '3 × 90-minute private antenatal sessions',
      'Optional 2 × 45-minute birth partner coaching sessions',
      'Personalised Empowerment Journey Guide booklet',
      'Tailored guidance to your unique journey, preferences, and needs',
    ],
    pricing: [
      { amount: '£595', label: 'Full Empowerment Experience (including partner sessions)' },
      { amount: '£495', label: 'Core Experience (without partner sessions)' },
    ],
    closing:
      'This is your time to prepare for birth in a way that feels aligned, supported, and empowering.',
  },
  {
    id: 'debrief',
    number: '02',
    title: 'Birth Healing Debrief Sessions',
    availability: 'Available from 4 weeks postnatal — In person (ideal) or Online',
    intro:
      'Birth can be powerful, beautiful and sometimes overwhelming or difficult to process. If your experience felt confusing, intense, or left you with unresolved emotions, you are not alone and you deserve space for healing.',
    description:
      'H.E.R Birth Healing Debrief Sessions offer gentle, trauma-informed, 1:1 holding space where your story can be heard without judgement. Together, we will gently unpack your birth story at your pace, allowing space for reflection, emotional release, and deeper understanding. This work goes beyond your birth — it also honours your postpartum identity, creating space to explore how your experience may have shaped your feelings, confidence, and transition into motherhood.',
    partnerNote: null,
    includes: [
      '3 × 60-minute private 1:1 healing sessions',
      'Trauma-informed emotional processing and support',
      'Nervous system regulation tools',
      'Signposting to local specialists and services within your area',
    ],
    pricing: [
      { amount: '£295', label: '3-session Birth Healing Debrief Package' },
      { amount: '£85ph', label: 'Additional 60-minute session' },
    ],
    closing:
      'This is your space to pause, process, and begin to heal — gently, safely, and at your own pace.',
  },
  {
    id: 'postpartum',
    number: '03',
    title: 'H.E.R Restoration Postpartum Home Visits',
    availability: 'Available In-Home — Essex, North Kent & East London',
    intro:
      'The early days after birth are a time of deep transition — physically, emotionally, and mentally. Yet many mothers, fathers, and family members are expected to navigate this period without the consistent, hands-on support they truly need.',
    description:
      'H.E.R Restoration Postpartum Home Visits are designed to gently hold you during this time, offering calm, compassionate support so you can rest, recover, and adjust to life with your baby without pressure or overwhelmed. Each visit blends practical care with emotional presence, supporting your body recovery while also creating space for emotional check-ins, reassurance, and connection.',
    partnerNote:
      'There is also space to gently support fathers or birth partners, offering guidance and reassurance so they feel more confident in how to care for and support you during your recovery.',
    includes: [
      '3 in-home visits (3 hours per visit)',
      'Dedicated time for rest and recovery',
      'Infant feeding and lactation support',
      'Light home help and meal preparation',
      'Emotional check-ins and nervous system support',
      'Gentle guidance for body recovery',
    ],
    pricing: [
      { amount: '£495', label: '3 postpartum home visits' },
      { amount: '£55 per hour', label: 'Additional ad hoc support' },
    ],
    closing:
      'This is your time to be cared for — to rest, to recover, and to feel supported as you step into motherhood and parenthood with confidence and ease.',
  },
]

type Service = (typeof services)[0]

const ServiceBlock = ({ service, flipped }: { service: Service; flipped: boolean }) => {
  const { ref, inView } = useInView<HTMLDivElement>()

  return (
    <section className={flipped ? 'bg-base-50' : 'bg-white'}>
      <div ref={ref} className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div
          className={`flex flex-col lg:flex-row gap-12 lg:gap-20 ${flipped ? 'lg:flex-row-reverse' : ''}`}
        >
          {/* Number panel */}
          <div
            className={`lg:w-[220px] shrink-0 flex flex-row lg:flex-col items-center lg:items-start gap-4 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
          >
            <span
              className="font-display font-bold text-gold-500 opacity-25 leading-none select-none"
              style={{ fontSize: 'clamp(5rem, 10vw, 8rem)' }}
              aria-hidden="true"
            >
              {service.number}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-crimson-600 bg-crimson-50 px-3 py-1.5 rounded-full">
              {service.availability.split('—')[0].trim()}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-7">
            <div
              className={inView ? 'animate-fade-in-up' : 'opacity-0'}
              style={{ animationDelay: inView ? '0.1s' : undefined }}
            >
              <h2
                className="font-display font-bold text-base-900 leading-tight mb-4"
                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)' }}
              >
                {service.title}
              </h2>
              <p className="font-display italic text-base-600 text-[1.05rem] leading-relaxed border-l-[3px] border-gold-400 pl-4">
                {service.intro}
              </p>
            </div>

            <div
              className={`w-10 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 ${inView ? 'animate-fade-in' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.15s' : undefined }}
            />

            <p
              className={`text-[15px] text-base-600 leading-[1.9] ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.2s' : undefined }}
            >
              {service.description}
            </p>

            {service.partnerNote && (
              <div
                className={`bg-crimson-50 border border-crimson-100 rounded-xl px-5 py-4 text-[14px] text-base-600 leading-relaxed ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: inView ? '0.25s' : undefined }}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-crimson-600 mb-2">
                  For Partners
                </p>
                {service.partnerNote}
              </div>
            )}

            {/* Includes + Pricing side by side on desktop */}
            <div
              className={`flex flex-col sm:flex-row gap-8 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.3s' : undefined }}
            >
              <div className="flex-1">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-base-400 mb-4">
                  What's Included
                </p>
                <ul className="space-y-2.5">
                  {service.includes.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-crimson-50 flex items-center justify-center shrink-0">
                        <RiCheckLine size={11} className="text-crimson-600" />
                      </span>
                      <span className="text-[13.5px] text-base-700 leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sm:w-[220px] shrink-0">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-base-400 mb-4">
                  Pricing
                </p>
                <div className="bg-white rounded-xl border border-base-100 px-5 py-4 shadow-[0_4px_14px_-4px_rgba(155,28,28,0.08)] space-y-3">
                  {service.pricing.map((p) => (
                    <div key={p.label}>
                      <p className="font-display font-bold text-gold-500 text-2xl leading-none">
                        {p.amount}
                      </p>
                      <p className="text-[11.5px] text-base-500 mt-0.5 leading-snug">{p.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Closing line + CTA */}
            <div
              className={`flex flex-col sm:flex-row sm:items-center gap-5 pt-2 border-t border-base-100 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.4s' : undefined }}
            >
              <p className="font-display italic text-base-500 text-[0.95rem] leading-snug flex-1">
                {service.closing}
              </p>
              <Link to={ROUTES.ENQUIRY} className="btn-outline shrink-0">
                Book a Support Call
                <RiArrowRightLine size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export const ServicesListSection = () => (
  <>
    {services.map((service, i) => (
      <ServiceBlock key={service.id} service={service} flipped={i % 2 !== 0} />
    ))}
  </>
)
